import "@/App.css";
import "@/styles/editorial.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Home from "./pages/Home";
import Today from "./pages/Today";
import Explore from "./pages/Explore";
import { LegacyDestination } from "./components/dnh/feed/LegacyDestination";
import Country from "./pages/Country";
import Category from "./pages/Category";
import Search from "./pages/Search";
import SearchTopicBrief from "./pages/SearchTopicBrief";
import SearchArticleBrief from "./pages/SearchArticleBrief";
import KnowledgeBriefPage from "./pages/KnowledgeBriefPage";
import TopicDetail from "./pages/TopicDetail";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import Account from "./pages/Account";
import About from "./pages/About";
import Admin from "./pages/Admin";
import BriefDetail from "./pages/BriefDetail";
import PublicBrief from "./pages/PublicBrief";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import Subscribe from "./pages/Subscribe";
import SubscribeSuccess from "./pages/SubscribeSuccess";
import StateBriefPage from "./pages/StateBriefPage";
import StateIndexPage from "./pages/StateIndexPage";
import SeeThroughLens from "./pages/SeeThroughLens";
import SavedStories from "./pages/SavedStories";
import ForYou from "./pages/ForYou";
import SourcesPage from "./pages/Sources";
import NotFound from "./pages/NotFound";
import Unsubscribe from "./pages/Unsubscribe";
import SourceDetail from "./pages/SourceDetail";
import { ShareCardPreview } from "./components/dnh/ShareCard";
import { Toaster } from "@/components/ui/sonner";
import { SearchShortcut } from "./components/dnh/search/SearchNavigation";
import { AuthProvider, useAuth } from "./components/dnh/AuthContext";
import { BookmarkProvider } from "./components/dnh/BookmarkContext";

/** Guards /admin — silently redirects non-admins to home */
function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--paper)] flex flex-col">
        <div className="h-[56px] border-b border-[var(--hairline)] animate-pulse bg-[var(--surface-2)]" />
        <div className="h-[38px] border-b border-[var(--hairline)] animate-pulse bg-[var(--paper)]" />
        <div className="flex-1 flex items-center justify-center">
          <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--muted)]">Verifying access…</div>
        </div>
      </div>
    );
  }
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return children;
}

const PWA_RESUME_KEY = "dnh_pwa_last_path";
const SKIP_RESTORE = new Set(["/signin", "/signup", "/auth/google", "/subscribe", "/subscribe/success"]);

/**
 * PwaSessionRestore — iOS kills PWA processes in the background and restarts
 * from "/". This hook saves the current path on every navigation and restores
 * it on the next cold start when running in standalone (home-screen) mode.
 */
function PwaSessionRestore() {
  const location = useLocation();
  const navigate = useNavigate();

  // On mount: if standalone + saved path exists, restore it once
  useEffect(() => {
    const isStandalone =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;
    if (!isStandalone) return;
    const saved = localStorage.getItem(PWA_RESUME_KEY);
    if (!saved || saved === "/" || SKIP_RESTORE.has(saved.split("?")[0])) return;
    // Only restore if we're landing on "/" (cold start)
    if (window.location.pathname === "/") {
      navigate(saved, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On every navigation: persist the path
  useEffect(() => {
    const path = location.pathname + location.search;
    if (!SKIP_RESTORE.has(location.pathname)) {
      localStorage.setItem(PWA_RESUME_KEY, path);
    }
  }, [location]);

  return null;
}

/**
 * AppRouter: Detects session_id in URL fragment synchronously (before useEffect)
 * to prevent race conditions with the AuthProvider /me check.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <PwaSessionRestore />
      <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/today" element={<Today />} />
      <Route path="/country/:slug" element={<Country />} />
      <Route path="/countries" element={<LegacyDestination kind="countries" />} />
      <Route path="/explore" element={<Explore />} />
      <Route path="/category/:slug" element={<Category />} />
      <Route path="/search" element={<Search />} />
      <Route path="/search/topic" element={<SearchTopicBrief />} />
      <Route path="/search/article/:id" element={<SearchArticleBrief />} />
      <Route path="/knowledge/:id/brief" element={<KnowledgeBriefPage />} />
      <Route path="/topics" element={<LegacyDestination kind="topics" />} />
      <Route path="/topics/:slug" element={<TopicDetail />} />
      <Route path="/arc/:slug" element={<LegacyDestination kind="arc" />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/login" element={<Navigate to="/signin" replace />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/account" element={<Account />} />
      <Route path="/about" element={<About />} />
      <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
      <Route path="/world" element={<LegacyDestination kind="world" />} />
      <Route path="/see-through-lens" element={<SeeThroughLens />} />
      <Route path="/perspectives" element={<Navigate to={{ pathname: "/see-through-lens", search: location.search, hash: location.hash }} replace />} />
      <Route path="/highlights" element={<LegacyDestination kind="latest" />} />
      <Route path="/highlights/archive" element={<LegacyDestination kind="archive" />} />
      <Route path="/saved" element={<Navigate to="/for-you" replace />} />
      <Route path="/for-you" element={<ForYou />} />
      <Route path="/sources" element={<SourcesPage />} />
      <Route path="/sources/:sourceId" element={<SourceDetail />} />
      <Route path="/briefs" element={<LegacyDestination kind="archive" />} />
      <Route path="/briefs/:id" element={<BriefDetail />} />
      <Route path="/reader" element={<LegacyDestination kind="reader" />} />
      <Route path="/subscribe" element={<Subscribe />} />
      <Route path="/subscribe/success" element={<SubscribeSuccess />} />
      <Route path="/auth/google" element={<GoogleAuthCallback />} />
      <Route path="/brief/:scope/:slug" element={<PublicBrief />} />
      <Route path="/brief/story/:id/:date" element={<LegacyDestination kind="story" />} />
      <Route path="/brief/:scope/:slug/:date" element={<PublicBrief />} />
      <Route path="/state/:countryIso2/:stateSlug" element={<StateBriefPage />} />
      <Route path="/state/:countryIso2" element={<StateIndexPage />} />
      <Route path="/share-card-preview" element={<ShareCardPreview />} />
      <Route path="*" element={<NotFound />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BookmarkProvider>
        <div className="App">
          <BrowserRouter>
            <AppRouter />
            <SearchShortcut />
          </BrowserRouter>
          <Toaster
            position="bottom-right"
            theme="light"
            toastOptions={{
              style: {
                background: "var(--paper)",
                color: "var(--ink)",
                border: "1px solid var(--hairline-strong, #D8D2C6)",
                fontFamily:
                  "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif",
              },
            }}
          />
        </div>
      </BookmarkProvider>
    </AuthProvider>
  );
}

export default App;
